import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { formatearRNC, limpiarRNC } from '@/lib/rnc'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoTexto } from '@/ui/CampoFormulario'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { Monto } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import { MOTIVO_MAXIMO, MOTIVO_MINIMO } from '@/consola/DialogoConMotivo'
import { PRESENTACION_ETAPA } from '@/dominio/cuenta/tipos'
import { traducirErrorConsola } from '@/dominio/consola/useConsola'
import { useCrearDespacho, useDespachosConsola } from '@/dominio/despacho/useDespachosConsola'
import type { DespachoEnConsola } from '@/dominio/despacho/tipos'
import { EncabezadoPantalla, Tarjeta } from '../../-pantalla'
import { ErrorDeCarga, SoloDueno, entero, useEsDueno } from '../-comun'

/**
 * Los despachos contables de la plataforma (`docs/09` §5.9, `docs/adr/0021`):
 * quiénes son, cuántos clientes llevan, cuántos esperan respuesta y cómo va su
 * cuenta con Agentical Group. Cada fila lleva a su ficha, con sus vínculos y su
 * auditoría.
 *
 * El alta desde aquí nace **verificada**: la hace Agentical Group. La del portal nace sin
 * verificar y se verifica en la ficha.
 */

interface BusquedaDespachos {
  buscar?: string
  estado?: string
}

const TAMANO = 50

export const Route = createFileRoute('/consola/despachos/')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaDespachos => ({
    buscar: typeof busqueda.buscar === 'string' && busqueda.buscar !== '' ? busqueda.buscar : undefined,
    estado: typeof busqueda.estado === 'string' && busqueda.estado !== '' ? busqueda.estado : undefined,
  }),
  component: PantallaDespachos,
})

const TONO_ESTADO = { activo: 'verde', suspendido: 'ambar', cerrado: 'neutral' } as const

function PantallaDespachos() {
  const { buscar, estado } = Route.useSearch()
  const navegar = Route.useNavigate()
  const dueno = useEsDueno()
  const [pagina, setPagina] = useState(0)
  const [creando, setCreando] = useState(false)
  const consulta = useDespachosConsola({ buscar, estado, limite: TAMANO, saltar: pagina * TAMANO })
  const filas = consulta.data?.filas ?? []
  const total = consulta.data?.total ?? 0
  const th = 'h-[var(--alto-fila)] px-2 text-left text-etiqueta font-semibold text-gris-600'

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Despachos"
        descripcion="Los despachos contables: su equipo, los clientes que llevan con su consentimiento y su cuenta con Agentical Group."
        acciones={
          dueno ? (
            <Boton variante="primario" icono={<Plus />} onClick={() => setCreando(true)}>
              Nuevo despacho
            </Boton>
          ) : (
            <SoloDueno />
          )
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          defaultValue={buscar ?? ''}
          onChange={(e) => {
            setPagina(0)
            void navegar({ search: (p: BusquedaDespachos) => ({ ...p, buscar: e.target.value || undefined }), replace: true })
          }}
          placeholder="Nombre, RNC o correo"
          aria-label="Buscar despachos"
          className="h-[var(--alto-control-menor)] w-[260px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo"
        />
        <select
          value={estado ?? ''}
          onChange={(e) => {
            setPagina(0)
            void navegar({ search: (p: BusquedaDespachos) => ({ ...p, estado: e.target.value || undefined }), replace: true })
          }}
          aria-label="Estado"
          className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta"
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="suspendido">Suspendidos</option>
          <option value="cerrado">Cerrados</option>
        </select>
        <span className="text-etiqueta text-gris-600">{entero(total)} despacho(s)</span>
      </div>

      <Tarjeta relleno={false}>
        {consulta.isError ? (
          <ErrorDeCarga error={consulta.error} queCargaba="los despachos" onReintentar={() => void consulta.refetch()} />
        ) : !consulta.isLoading && filas.length === 0 ? (
          <EstadoVacio variante="sin-resultados" mensaje="Ningún despacho con esos filtros." />
        ) : (
          <table className="w-full border-collapse text-cuerpo" data-densidad="normal">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300">
                <th scope="col" className={th}>Despacho</th>
                <th scope="col" className={`${th} w-[120px]`}>Estado</th>
                <th scope="col" className={`${th} w-[90px] text-right`}>Equipo</th>
                <th scope="col" className={`${th} w-[90px] text-right`}>Clientes</th>
                <th scope="col" className={`${th} w-[100px] text-right`}>Pendientes</th>
                <th scope="col" className={`${th} w-[150px]`}>Cuenta</th>
                <th scope="col" className={`${th} w-[110px]`}>Alta</th>
              </tr>
            </thead>
            <tbody>
              {consulta.isLoading ? (
                <FilasEsqueleto filas={6} columnas={7} />
              ) : (
                filas.map((d) => <Fila key={d.id} despacho={d} />)
              )}
            </tbody>
          </table>
        )}
      </Tarjeta>

      {total > TAMANO ? (
        <div className="flex items-center gap-2">
          <Boton tamano="menor" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </Boton>
          <span className="text-etiqueta text-gris-600">
            Página {pagina + 1} de {Math.ceil(total / TAMANO)}
          </span>
          <Boton tamano="menor" disabled={(pagina + 1) * TAMANO >= total} onClick={() => setPagina((p) => p + 1)}>
            Siguiente
          </Boton>
        </div>
      ) : null}

      <PanelNuevo abierto={creando} onCerrar={() => setCreando(false)} />
    </div>
  )
}

function Fila({ despacho: d }: { despacho: DespachoEnConsola }) {
  const etapa = d.etapa ? PRESENTACION_ETAPA[d.etapa] : null
  return (
    <tr className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
      <td className="px-2">
        <Link to="/consola/despachos/$id" params={{ id: d.id }} className="flex min-w-0 flex-col hover:text-marca">
          <span className="truncate text-gris-900">{d.nombre}</span>
          <span className="numero text-etiqueta text-gris-600">
            {d.tipoIdentificacion === 'rnc' ? 'RNC' : 'Cédula'} {formatearRNC(d.identificacion)} · {d.correo}
            {d.verificado ? '' : ' · sin verificar'}
          </span>
        </Link>
      </td>
      <td className="px-2">
        <InsigniaEstado tono={TONO_ESTADO[d.estado]} aclaracion={d.origen === 'portal' ? 'portal' : undefined}>
          {d.estado}
        </InsigniaEstado>
      </td>
      <td className="numero px-2 text-right">{entero(d.miembros)}</td>
      <td className="numero px-2 text-right">{entero(d.clientes)}</td>
      <td className="numero px-2 text-right">{entero(d.pendientes)}</td>
      <td className="px-2">
        {etapa ? (
          <span className="flex flex-col">
            <InsigniaEstado tono={etapa.tono}>{etapa.etiqueta}</InsigniaEstado>
            {d.saldo && d.saldo !== '0.00' ? <Monto valor={d.saldo} moneda="DOP" className="text-etiqueta" /> : null}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="numero px-2 text-gris-700">{formatearFecha(d.creadoEl)}</td>
    </tr>
  )
}

function PanelNuevo({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const crear = useCrearDespacho()
  const [nombre, setNombre] = useState('')
  const [identificacion, setIdentificacion] = useState('')
  const [correo, setCorreo] = useState('')
  const [titular, setTitular] = useState('')
  const [credencial, setCredencial] = useState('')
  const [motivo, setMotivo] = useState('')

  const digitos = limpiarRNC(identificacion)
  const listo =
    nombre.trim().length >= 3 &&
    (digitos.length === 9 || digitos.length === 11) &&
    correo.includes('@') &&
    titular.includes('@') &&
    motivo.trim().length >= MOTIVO_MINIMO &&
    motivo.trim().length <= MOTIVO_MAXIMO

  const cerrar = (): void => {
    crear.reset()
    onCerrar()
  }

  return (
    <PanelLateral
      abierto={abierto}
      onCerrar={cerrar}
      titulo="Nuevo despacho"
      subtitulo="Nace verificado, con su titular"
      pie={
        <>
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton
            variante="primario"
            disabled={!listo || crear.isPending}
            onClick={() =>
              crear.mutate(
                {
                  nombre: nombre.trim(),
                  identificacion: digitos,
                  correo: correo.trim(),
                  titularEmail: titular.trim(),
                  credencialInicial: credencial === '' ? undefined : credencial,
                  motivo: motivo.trim(),
                },
                {
                  onSuccess: () => {
                    setNombre('')
                    setIdentificacion('')
                    setCorreo('')
                    setTitular('')
                    setCredencial('')
                    setMotivo('')
                    onCerrar()
                  },
                },
              )
            }
          >
            {crear.isPending ? 'Creando…' : 'Crear'}
          </Boton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <CampoTexto etiqueta="Nombre" obligatorio value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <CampoTexto
          etiqueta="RNC, o cédula si es independiente"
          obligatorio
          numerico
          inputMode="numeric"
          value={identificacion}
          onChange={(e) => setIdentificacion(e.target.value)}
        />
        <CampoTexto etiqueta="Correo del despacho" obligatorio type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} />
        <CampoTexto
          etiqueta="Correo del titular"
          obligatorio
          type="email"
          value={titular}
          onChange={(e) => setTitular(e.target.value)}
          ayuda="Si ya tiene cuenta en la plataforma, entra con la suya."
        />
        <CampoTexto
          etiqueta="Credencial inicial del titular"
          anotacion="solo si no tiene cuenta"
          type="password"
          autoComplete="new-password"
          value={credencial}
          onChange={(e) => setCredencial(e.target.value)}
        />
        <CampoTexto
          etiqueta="Motivo"
          obligatorio
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          ayuda={`De ${MOTIVO_MINIMO} a ${MOTIVO_MAXIMO} caracteres. Queda en la auditoría.`}
        />
        {crear.isError ? (
          <Banner tono="rojo" titulo={traducirErrorConsola(crear.error, 'crear el despacho').titulo}>
            {traducirErrorConsola(crear.error, 'crear el despacho').contexto}
          </Banner>
        ) : null}
      </div>
    </PanelLateral>
  )
}
